// src/services/WalkieService.js
/**
 * WalkieService - WebRTC P2P Voice Communication
 *
 * Uses Firestore for signaling (no server required)
 * Direct browser-to-browser audio streaming
 */

import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  getDocs,
  writeBatch,
} from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/services/firebaseconfig'
import { fleetWalkieControl, isFleetPeer } from '@/services/FleetWalkieControl'

// How often an answered call checks its stats for the caller's first audio, for a browser that never fires `unmute`.
const MEDIA_POLL_MS = 250

// ── Signal protocol v2 (docs/features/native_comms_suite_FEATURE.md Phase 3) ─────────────────────────────────────
// A person's Walkie queue is read by every device they are signed in on: browser tabs and their iPhone, which a VoIP
// push wakes a second or two after the offer lands. So a device never deletes a fresh signal it did not use: it
// deletes the signals of a call it handled once that call ends here, and any signal older than SIGNAL_STALE_MS. The
// device that answers or declines tells the person's other devices with a `handled` signal in their own queue.
export const SIGNAL_STALE_MS = 2 * 60 * 1000

function signalTime(signal) {
  const at = signal?.createdAt
  if (at && typeof at.toMillis === 'function') return at.toMillis()
  if (at instanceof Date) return at.getTime()
  return null // a write still pending on this device
}

/** Older than SIGNAL_STALE_MS: its call is over, so it is never acted on (and may be deleted by its queue's owner). */
export function isStaleSignal(signal, now = Date.now()) {
  const at = signalTime(signal)
  return at !== null && now - at > SIGNAL_STALE_MS
}

/**
 * The calls in one batch of the queue that are already settled for this device: answered or declined on another of
 * this person's devices ('handled'), or hung up / declined by the caller's side ('ended'). Read before ringing, so a
 * reload never rings for a call that is over or was taken elsewhere.
 */
export function settledCalls(signals, myUid, deviceId) {
  const settled = new Map()
  for (const signal of signals) {
    if (signal.type === 'handled' && signal.fromUid === myUid && signal.deviceId !== deviceId) {
      settled.set(signal.callId, 'handled')
    } else if (
      (signal.type === 'hangup' || signal.type === 'decline') &&
      !settled.has(signal.callId)
    ) {
      settled.set(signal.callId, 'ended')
    }
  }
  return settled
}

class WalkieService {
  constructor() {
    this.peerConnection = null
    this.localStream = null
    this.remoteStream = null
    this.signalUnsubscribe = null

    this.currentCallId = null
    this.currentPeerUid = null
    this.isCaller = false
    this.pendingIceCandidates = []
    // The accept under way ({ callId, promise }) and the call this tab answered: each call is answered once.
    this.accepting = null
    this.answeredCallId = null
    // An answered call's wait for the caller's audio (_reportWhenMediaFlows); one per connection.
    this.mediaWatch = null
    this._audioOwnership = null
    this._mediaLease = null
    this._drainingMedia = new Set()

    // Cross-tab communication
    this.broadcastChannel = null
    this._setupBroadcastChannel()
    // This tab's id: in BroadcastChannel messages and in the `handled` signals this tab writes for its owner's devices.
    if (!this._tabId) this._tabId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    // Callbacks
    this.onIncomingCall = null // (fromUid, fromName, callId) => {}
    this.onCallConnected = null // () => {}
    this.onCallEnded = null // (reason) => {}
    this.onRemoteStream = null // (stream) => {}
    this.onError = null // (error) => {}

    // ICE servers - will be populated with TURN credentials from Twilio
    // Fallback to STUN-only if TURN fetch fails
    this.iceServers = [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ]
    this.iceServersFetched = false
  }

  /**
   * Set up cross-tab communication for call state sync
   */
  _setupBroadcastChannel() {
    if (typeof BroadcastChannel === 'undefined') {
      console.warn('[WalkieService] BroadcastChannel not supported')
      return
    }

    this.broadcastChannel = new BroadcastChannel('walkie-talkie')

    this.broadcastChannel.onmessage = event => {
      const { type, callId, tabId } = event.data

      // Ignore messages from our own tab
      if (tabId === this._tabId) return

      console.log('[WalkieService] Broadcast received:', type, 'for call:', callId)

      // Only about the call this tab is on (a broadcast for no call, or another call, is not ours).
      if (!callId || callId !== this.currentCallId) return

      if (type === 'call-answered') {
        // Another tab answered this call - stop ringing and clean up
        console.log('[WalkieService] Call answered on another tab, cleaning up')
        this._cleanupForOtherTabAnswered()
      } else if (type === 'call-ended') {
        // Another tab hung up or declined this call
        console.log('[WalkieService] Call ended on another tab')
        this._cleanup()
        this.onCallEnded?.('answered_elsewhere', callId)
      }
    }

    // Generate unique tab ID
    this._tabId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Broadcast a message to other tabs
   */
  _broadcast(type, data = {}) {
    if (!this.broadcastChannel) return

    this.broadcastChannel.postMessage({
      type,
      tabId: this._tabId,
      callId: this.currentCallId,
      ...data,
    })
  }

  /**
   * Clean up when another tab answered the call
   */
  _cleanupForOtherTabAnswered() {
    const callId = this.currentCallId
    // Stop local tracks but don't send hangup signal
    this._cleanup(false)
    // Notify store to stop ringing
    this.onCallEnded?.('answered_elsewhere', callId)
  }

  /**
   * Fetch TURN credentials from Twilio via cloud function
   */
  async fetchTurnCredentials() {
    if (this.iceServersFetched) return

    try {
      const getTurn = httpsCallable(functions, 'getTurnCredentials')
      const result = await getTurn()

      if (result.data?.iceServers) {
        this.iceServers = result.data.iceServers
        this.iceServersFetched = true
        console.log('[WalkieService] TURN credentials fetched successfully')
      }
    } catch (e) {
      console.warn('[WalkieService] Could not fetch TURN credentials, using STUN only:', e)
      // Keep default STUN servers as fallback
    }
  }

  /**
   * Initialize the service for a user - start listening for incoming calls
   */
  startListening(myUid) {
    this.myUid = myUid

    // Fetch TURN credentials from Twilio
    this.fetchTurnCredentials()

    // Listen to my signals collection for incoming offers/answers/candidates
    const signalsRef = collection(db, 'walkieSignals', myUid, 'incoming')

    this.signalUnsubscribe = onSnapshot(
      query(signalsRef, orderBy('createdAt', 'asc')),
      snapshot => {
        const added = snapshot
          .docChanges()
          .filter(change => change.type === 'added')
          .map(change => ({ id: change.doc.id, ...change.doc.data() }))
          // A group call's signals (and its invites) belong to GroupWalkieService.
          .filter(signal => !signal.roomId)
        const settled = settledCalls(added, this.myUid, this._tabId)
        added.forEach(signal => {
          this._handleSignal(signal, settled).catch(error => {
            if (signal.callId === this.currentCallId) this.hangup()
            this.onError?.(error)
          })
        })
      },
      error => {
        console.error('[WalkieService] Signal listener error:', error)
      }
    )

    console.log('[WalkieService] Listening for signals as:', myUid)
  }

  /**
   * Stop listening and clean up
   */
  stopListening() {
    if (this.signalUnsubscribe) {
      this.signalUnsubscribe()
      this.signalUnsubscribe = null
    }
    this.hangup()
  }

  /**
   * Initiate a call to another user
   */
  async call(peerUid, _peerName) {
    if (this.peerConnection || this.currentCallId) {
      console.warn('[WalkieService] Already in a call')
      return false
    }

    const callId = `${this.myUid}_${peerUid}_${Date.now()}`
    try {
      this._reserveAudio(callId)
    } catch (error) {
      this.onError?.(error)
      return false
    }
    // Hung up while this was awaiting (the microphone prompt, building the offer): stop before ringing anyone.
    const abandoned = () => this.currentCallId !== callId
    try {
      this.isCaller = true
      this.currentPeerUid = peerUid
      this.currentCallId = callId

      if (isFleetPeer(peerUid)) {
        await fleetWalkieControl('open', callId, { peerUid })
        if (abandoned()) {
          await fleetWalkieControl('close', callId)
          return this._releaseIfIdle()
        }
      }

      // Get microphone access
      await this._getLocalStream()
      if (abandoned()) return this._releaseIfIdle()

      // Create peer connection
      this._createPeerConnection()
      const pc = this.peerConnection

      // Add local tracks
      this.localStream.getTracks().forEach(track => {
        pc.addTrack(track, this.localStream)
      })

      // Create and send offer
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
      })

      await pc.setLocalDescription(offer)
      if (abandoned()) return this._releaseIfIdle()

      // Send offer via Firestore
      await this._sendSignal(peerUid, {
        type: 'offer',
        callId,
        fromUid: this.myUid,
        fromName: this._getMyName(),
        payload: JSON.stringify(offer),
      })

      console.log('[WalkieService] Offer sent to:', peerUid)
      return true
    } catch (error) {
      if (abandoned()) return this._releaseIfIdle()
      console.error('[WalkieService] Call failed:', error)
      // The offer never reached the peer: release quietly and report the error (the store's one end of the call).
      this._cleanup()
      this.onError?.(error)
      return false
    }
  }

  /** After an abandoned dial: a microphone granted after the hang-up must not stay open when no call is up. */
  _releaseIfIdle() {
    if (!this.currentCallId && !this.peerConnection) this._cleanup()
    return false
  }

  /**
   * Accept an incoming call. Once per call: a second Accept while the first is under way gets the first's result, and
   * one after the answer went does nothing. A call an AI placed rings on for a few seconds after Accept while its mind
   * readies the call; a second Accept then added the microphone again, the browser refused a second sender for the
   * track, and the call ended (docs/bugs/walkie_ai_call_accept_adds_microphone_twice_BUG.md).
   */
  acceptCall(callId, fromUid) {
    if (callId && this.answeredCallId === callId) return Promise.resolve(true)
    if (this.accepting && this.accepting.callId === callId) return this.accepting.promise
    const promise = this._acceptCall(callId, fromUid)
    this.accepting = { callId, promise }
    promise.finally(() => {
      if (this.accepting && this.accepting.promise === promise) this.accepting = null
    })
    return promise
  }

  async _acceptCall(callId, fromUid) {
    // Peer connection should already exist from handling the offer
    if (!this.peerConnection) {
      console.error('[WalkieService] No peer connection - offer not received')
      return false
    }

    // Verify this is the call we're expecting
    if (this.currentCallId !== callId || this.currentPeerUid !== fromUid) {
      console.warn('[WalkieService] Call ID mismatch')
      return false
    }

    try {
      this.isCaller = false
      this._reserveAudio(callId)
      const pc = this.peerConnection
      const abandoned = () => this.currentCallId !== callId || this.peerConnection !== pc
      if (isFleetPeer(fromUid)) {
        await fleetWalkieControl('accept', callId)
        if (abandoned()) return this._releaseIfIdle()
      }

      console.log('[WalkieService] Accepting call from:', fromUid)

      // Get microphone access
      await this._getLocalStream()
      if (abandoned()) return this._releaseIfIdle()

      // Add local tracks, each once: a track the connection already sends is never added again (the browser refuses it)
      console.log('[WalkieService] Adding local tracks...')
      const sending = new Set(pc.getSenders().map(sender => sender.track))
      this.localStream.getTracks().forEach(track => {
        if (!sending.has(track)) pc.addTrack(track, this.localStream)
      })

      // Create and send answer
      console.log('[WalkieService] Creating answer...')
      const answer = await this.peerConnection.createAnswer()
      if (abandoned()) return this._releaseIfIdle()

      console.log('[WalkieService] Setting local description...')
      await this.peerConnection.setLocalDescription(answer)
      if (abandoned()) return this._releaseIfIdle()

      console.log('[WalkieService] Sending answer to:', fromUid)
      await this._sendSignal(fromUid, {
        type: 'answer',
        callId: this.currentCallId,
        fromUid: this.myUid,
        payload: JSON.stringify(answer),
      })

      console.log('[WalkieService] Answer sent successfully')
      this.answeredCallId = callId

      // Broadcast to other tabs that we answered, and to this person's other devices (their iPhone, another browser)
      this._broadcast('call-answered')
      this._sendHandled(callId, 'answered')

      return true
    } catch (error) {
      console.error('[WalkieService] Accept failed:', error)
      this.hangup()
      this.onError?.(error)
      return false
    }
  }

  /**
   * Decline an incoming call
   */
  async declineCall(callId, fromUid) {
    // Release this tab's side of the offer, and stop this person's other tabs ringing (no hang-up follows a
    // decline). A declined offer that kept its peer connection made every later call look "already in a call":
    // incoming calls were auto-declined as busy and outgoing ones refused, until the page reloaded.
    if (callId && callId === this.currentCallId) {
      this._broadcast('call-ended')
      this._cleanup()
    }

    await this._sendSignal(fromUid, {
      type: 'decline',
      callId: callId,
      fromUid: this.myUid,
    })
    await this._sendHandled(callId, 'declined')
    this._purgeCall(callId)
  }

  /**
   * End the current call: tell this person's other tabs and the peer, release the media, and report the end once.
   * With no call up (stopListening() runs this at every sign-out) it only releases what is left and reports
   * nothing: a report with no call played the disconnect tone and wrote presence at sign-out.
   */
  async hangup() {
    const callId = this.currentCallId
    const peerUid = this.currentPeerUid
    const hadCall = Boolean(callId || this.peerConnection)

    // Broadcast to other tabs (reads currentCallId, so before the cleanup)
    if (hadCall) this._broadcast('call-ended')
    this._cleanup()
    if (!hadCall) return

    if (peerUid) {
      // Notify peer
      try {
        await this._sendSignal(peerUid, {
          type: 'hangup',
          callId,
          fromUid: this.myUid,
        })
      } catch (e) {
        // Ignore - peer may already be gone
      }
    }

    this.onCallEnded?.('local_hangup', callId)
  }

  /**
   * Toggle mute
   */
  setMuted(muted) {
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach(track => {
        track.enabled = !muted
      })
      return true
    }
    return false
  }

  /**
   * Check if currently in a call
   */
  isInCall() {
    return this.peerConnection !== null && this.currentPeerUid !== null
  }

  // ─────────────────────────────────────────────────────────────
  // Private Methods
  // ─────────────────────────────────────────────────────────────

  async _getLocalStream() {
    if (this.localStream) return
    const callId = this.currentCallId
    const owner = this._mediaLease
    if (owner) owner.pending++
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      })
      if (owner) owner.unconfirmedStream = true
      if (callId !== this.currentCallId || owner?.closed) {
        stream.getTracks().forEach(track => track.stop())
        if (owner) owner.unconfirmedStream = false
        return
      }
      this.localStream = stream
      if (owner) owner.unconfirmedStream = false
    } finally {
      if (owner) {
        owner.pending--
        this._finishAudio(owner)
      }
    }
  }

  setAudioOwnership(ownership) {
    this._audioOwnership = ownership
  }
  hasOwnedAudio() {
    return !!this._mediaLease || this._drainingMedia.size > 0
  }
  _reserveAudio(callId) {
    if (!this._audioOwnership) return
    if (this._mediaLease?.callId === callId && !this._mediaLease.closed) return
    const lease = this._audioOwnership.reserve('legacy', () => this.hangup())
    this._mediaLease = { callId, lease, pending: 0, closed: false }
  }
  _finishAudio(owner) {
    if (!owner.closed || owner.pending > 0 || owner.unconfirmedStream) return
    owner.lease.release()
    this._drainingMedia.delete(owner)
  }

  _createPeerConnection() {
    this.peerConnection = new RTCPeerConnection({
      iceServers: this.iceServers,
    })

    // Handle ICE candidates
    this.peerConnection.onicecandidate = event => {
      if (event.candidate && this.currentPeerUid) {
        this._sendSignal(this.currentPeerUid, {
          type: 'ice-candidate',
          callId: this.currentCallId,
          fromUid: this.myUid,
          payload: JSON.stringify(event.candidate),
        })
      }
    }

    // Handle connection state changes (this connection's own: an event from a connection this tab already released
    // is not the current call's)
    const pc = this.peerConnection
    pc.onconnectionstatechange = () => {
      if (pc !== this.peerConnection) return
      const state = pc.connectionState
      console.log('[WalkieService] Connection state:', state)

      if (state === 'connected') {
        // A call this tab placed is connected when its connection is. A call it ANSWERED is connected when the
        // caller's audio actually arrives: the connection comes up first, and for a call an AI placed the audio can
        // follow seconds later, so the panel said End over dead air (dh1hFe1IFtRoQmGuMMon).
        if (this.isCaller) this.onCallConnected?.()
        else this._reportWhenMediaFlows(pc)
      } else if (state === 'disconnected' || state === 'failed' || state === 'closed') {
        const callId = this.currentCallId
        this._cleanup()
        this.onCallEnded?.(state, callId)
      }
    }

    // Handle incoming audio stream
    this.peerConnection.ontrack = event => {
      console.log('[WalkieService] Remote track received')
      this.remoteStream = event.streams[0]
      this.onRemoteStream?.(this.remoteStream)
    }
  }

  /**
   * Report an answered call connected once the caller's audio is flowing: its first audio packet. A remote WebRTC
   * track starts muted and unmutes on the first RTP packet it receives; for a browser that never fires `unmute`, the
   * connection's own stats (inbound audio packets received) say the same. Reported once, and only while `pc` is
   * still this call's connection. The store times the wait out (walkieStore ANSWER_CONNECT_TIMEOUT_MS).
   */
  _reportWhenMediaFlows(pc) {
    if (this.mediaWatch && this.mediaWatch.pc === pc) return
    this._stopMediaWatch()
    const watch = { pc, done: false, timer: null, tracks: [], report: null }
    this.mediaWatch = watch
    watch.report = () => {
      if (watch.done || pc !== this.peerConnection) return
      this._stopMediaWatch()
      console.log('[WalkieService] Remote audio is flowing')
      this.onCallConnected?.()
    }
    watch.tracks = pc
      .getReceivers()
      .map(receiver => receiver.track)
      .filter(track => track && track.kind === 'audio')
    const live = track => track.readyState !== 'ended' && !track.muted
    if (watch.tracks.some(live)) return watch.report()
    watch.tracks.forEach(track => track.addEventListener('unmute', watch.report))
    const poll = async () => {
      if (watch.done || pc !== this.peerConnection) return
      try {
        const stats = await pc.getStats()
        let flowing = false
        stats.forEach(stat => {
          const audio = stat.kind === 'audio' || stat.mediaType === 'audio'
          if (stat.type === 'inbound-rtp' && audio && stat.packetsReceived > 0) flowing = true
        })
        if (flowing) return watch.report()
      } catch (e) {
        // The connection closed under the poll; its cleanup stops this watch.
      }
      if (!watch.done) watch.timer = setTimeout(poll, MEDIA_POLL_MS)
    }
    watch.timer = setTimeout(poll, MEDIA_POLL_MS)
  }

  _stopMediaWatch() {
    const watch = this.mediaWatch
    if (!watch) return
    watch.done = true
    clearTimeout(watch.timer)
    watch.tracks.forEach(track => track.removeEventListener('unmute', watch.report))
    this.mediaWatch = null
  }

  async _handleSignal(signal, settled = new Map()) {
    console.log('[WalkieService] Received signal:', signal.type, 'from:', signal.fromUid)

    // Over: never acted on, and this queue's owner clears it.
    if (isStaleSignal(signal)) {
      await this._deleteSignal(signal.id)
      return
    }

    // Answered or declined on another of this person's devices (or tab of another browser): stop ringing, no record.
    if (signal.type === 'handled') {
      if (
        signal.fromUid === this.myUid &&
        signal.deviceId !== this._tabId &&
        signal.callId &&
        signal.callId === this.currentCallId &&
        this.answeredCallId !== signal.callId &&
        !this.isCaller
      ) {
        this._cleanupForOtherTabAnswered()
      }
      return
    }

    // Firestore rules bind fromUid to the authenticated sender. A staff member
    // who knows a call ID still is not its peer and cannot end or alter it. Another call's signal is left in place:
    // it may be the call another of this person's devices is on.
    if (
      signal.type !== 'offer' &&
      (signal.fromUid !== this.currentPeerUid || signal.callId !== this.currentCallId)
    ) {
      return
    }

    switch (signal.type) {
      case 'offer':
        // Already over, or taken on another device, before this batch: nothing to ring for.
        if (settled.has(signal.callId)) return
        await this._handleOffer(signal)
        break

      case 'answer':
        await this._handleAnswer(signal)
        break

      case 'ice-candidate':
        await this._handleIceCandidate(signal)
        break

      case 'hangup':
      case 'decline':
        // Ends only the call it belongs to. A stale signal (an earlier call's, one sent for a dial that never
        // rang here, or a hang-up reaching this person's idle tabs) must not end the current call or report
        // an end with no call up.
        if (signal.callId && signal.callId === this.currentCallId) {
          this._cleanup()
          const reason =
            signal.type === 'hangup'
              ? 'remote_hangup'
              : signal.reason === 'busy'
                ? 'busy'
                : 'declined'
          this.onCallEnded?.(reason, signal.callId)
        }
        break
    }
  }

  async _handleOffer(signal) {
    if (signal.callId === this.currentCallId && signal.fromUid === this.currentPeerUid) return
    // Don't accept if already in a call (here, or a group Walkie call in this tab)
    if (this.peerConnection || this.currentCallId || this._audioOwnership?.ownerKind === 'group') {
      // This person is busy: their other tabs, which got the same offer, stop ringing for it too (no hang-up
      // follows a decline).
      this._broadcast('call-ended', { callId: signal.callId })
      await this._sendSignal(signal.fromUid, {
        type: 'decline',
        callId: signal.callId,
        fromUid: this.myUid,
        reason: 'busy',
      })
      await this._sendHandled(signal.callId, 'declined')
      return
    }

    // Store call info BEFORE the await: an end for this call (a hang-up in the same batch of signals, another tab's
    // busy decline) that lands while the offer is applied must find it.
    this.currentCallId = signal.callId
    this.currentPeerUid = signal.fromUid

    if (isFleetPeer(signal.fromUid)) {
      const call = await fleetWalkieControl('inspect', signal.callId)
      if (call.humanUid !== this.myUid || call.agentUid !== signal.fromUid) {
        throw new Error('This AI call does not belong to your account.')
      }
      if (this.currentCallId !== signal.callId) return
    }
    this._createPeerConnection()

    const offer = JSON.parse(signal.payload)
    const pc = this.peerConnection
    await pc.setRemoteDescription(new RTCSessionDescription(offer))
    // Ended while the offer was applied: nothing to ring for.
    if (this.currentCallId !== signal.callId || this.peerConnection !== pc) return
    await this._flushIceCandidates()
    if (this.currentCallId !== signal.callId || this.peerConnection !== pc) return

    // Notify UI of incoming call
    this.onIncomingCall?.(signal.fromUid, signal.fromName, signal.callId)
  }

  async _handleAnswer(signal) {
    if (!this.peerConnection || signal.callId !== this.currentCallId) {
      console.warn('[WalkieService] Received answer for unknown call')
      return
    }

    const answer = JSON.parse(signal.payload)
    const pc = this.peerConnection
    await pc.setRemoteDescription(new RTCSessionDescription(answer))
    if (this.currentCallId !== signal.callId || this.peerConnection !== pc) return
    await this._flushIceCandidates()
  }

  async _handleIceCandidate(signal) {
    if (signal.callId !== this.currentCallId) {
      return
    }

    try {
      const candidate = JSON.parse(signal.payload)
      if (!this.peerConnection?.remoteDescription) {
        if (this.pendingIceCandidates.length >= 64)
          throw new Error('Too many connection candidates')
        this.pendingIceCandidates.push(candidate)
      } else await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate))
    } catch (e) {
      console.warn('[WalkieService] Failed to add ICE candidate:', e)
    }
  }

  async _flushIceCandidates() {
    const pc = this.peerConnection
    const pending = this.pendingIceCandidates.splice(0)
    for (const candidate of pending) {
      if (pc !== this.peerConnection) return
      await pc.addIceCandidate(new RTCIceCandidate(candidate))
    }
  }

  async _sendSignal(toUid, data) {
    const signalRef = doc(collection(db, 'walkieSignals', toUid, 'incoming'))
    await setDoc(signalRef, {
      ...data,
      fromUid: this.myUid,
      createdAt: serverTimestamp(),
    })
  }

  /** Tells this person's other devices that this one answered or declined the call (firestore.rules: own queue only). */
  async _sendHandled(callId, outcome) {
    if (!this.myUid || !callId) return
    try {
      await setDoc(doc(collection(db, 'walkieSignals', this.myUid, 'incoming')), {
        type: 'handled',
        callId,
        outcome,
        deviceId: this._tabId,
        fromUid: this.myUid,
        createdAt: serverTimestamp(),
      })
    } catch (e) {
      console.warn('[WalkieService] Could not tell my other devices about this call:', e)
    }
  }

  /** Clears the signals of a call this tab handled, once it has ended here. */
  async _purgeCall(callId) {
    if (!this.myUid || !callId) return
    try {
      const snap = await getDocs(
        query(
          collection(db, 'walkieSignals', this.myUid, 'incoming'),
          where('callId', '==', callId)
        )
      )
      const batch = writeBatch(db)
      snap.docs.forEach(d => batch.delete(d.ref))
      await batch.commit()
    } catch (e) {
      // Ignore: the stale sweep clears it later
    }
  }

  async _deleteSignal(signalId) {
    try {
      await deleteDoc(doc(db, 'walkieSignals', this.myUid, 'incoming', signalId))
    } catch (e) {
      // Ignore
    }
  }

  _cleanup(closeFleet = true) {
    const callId = this.currentCallId
    const peerUid = this.currentPeerUid
    const handledHere =
      Boolean(callId) && closeFleet && (this.isCaller || this.answeredCallId === callId)
    this._stopMediaWatch()
    // Stop local tracks
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop())
      this.localStream = null
    }

    // Close peer connection
    if (this.peerConnection) {
      this.peerConnection.close()
      this.peerConnection = null
    }

    // WebRTC close() and local track stop() above are synchronous. An unresolved
    // microphone permission request still owns its lease until its returned
    // stream has been stopped (or its rejection has settled) in finally.
    const owner = this._mediaLease
    this._mediaLease = null
    if (owner) {
      owner.closed = true
      this._drainingMedia.add(owner)
      this._finishAudio(owner)
    }

    this.remoteStream = null
    this.currentCallId = null
    this.currentPeerUid = null
    this.isCaller = false
    this.accepting = null
    this.answeredCallId = null
    this.pendingIceCandidates = []
    if (closeFleet && callId && isFleetPeer(peerUid)) {
      fleetWalkieControl('close', callId).catch(() => {})
    }
    if (handledHere) this._purgeCall(callId)
  }

  _getMyName() {
    // This will be set by the store when initializing
    return this._myName || 'Unknown'
  }

  setMyName(name) {
    this._myName = name
  }
}

// Singleton instance
export const walkieService = new WalkieService()
export default walkieService
